import { useRef } from "react"
import clsx from "clsx"
import { useWindowSize } from "usehooks-ts"

import type { UnlikelyCartLineInput } from "@unlikelystudio/commerce-connector"

import type { Nullish } from "~/@types/generics"
import { truncateText } from "~/lib/storyblok/utils/truncate-richtext"
import { AddToCartCta, type AddtoCartCtaProps } from "~/components/ui/AddToCartCta"
import CloseButton from "~/components/ui/CloseButton"
import type { TProduct, TVariant } from "~/components/ui/ProductHeader/_data/types"
import { type ProductDetailsCtaProps } from "~/components/ui/ProductHeader/components/ProductDetails/components/ProductDetailsCtas"
import * as css from "~/components/ui/ProductHeader/components/ProductDetails/components/ProductDetailsCtas/styles.css"
import type { TSizeGuideSlice } from "~/components/slices/SizeGuideSlice/types"
import type { CartLineItemPayload } from "~/providers/GTMTrackingProvider/constants"
import { useTranslate } from "~/providers/I18nProvider/hooks/useTranslate"
import { useIsFixedPanelOpen } from "~/managers/AddToCartFixedPanelManager/hooks"
import ProductDetailsPriceWithVariantSwitch from "~/app/[locale]/products/[slug]/(components)/ProductDetailsPriceWithVariantSwitch"
import VariantsRowWithVariantSwitch from "~/app/[locale]/products/[slug]/(components)/VariantsRowWithVariantSwitch"

import { sprinkles } from "~/styles/sprinkles.css"
import { hideFrom } from "~/styles/utils/show-hide"
import { breakpoints } from "~/styles/variables/breakpoints"
import { text } from "~/styles/variants"

type StickyAddToCartPopinProps = {
  shouldDisplayStockAlert: boolean
  activeVariants: TVariant[] | TVariant | null
  variants: TVariant[]
  sizeGuide: Nullish<TSizeGuideSlice>
  sizeGuideDisabled: boolean
  productType: TProduct["productType"]
  defaultVariant: TVariant | null
  collectionsHandles: TProduct["collectionsHandles"]
  trackingData: CartLineItemPayload
  checkoutItems: UnlikelyCartLineInput[]
  canShowFixedPanel: boolean
} & Pick<AddtoCartCtaProps, "productOutOfStock" | "isOutOfStock"> &
  Pick<ProductDetailsCtaProps, "product" | "trackingData">

export function StickyAddToCartPopin({
  shouldDisplayStockAlert,
  activeVariants,
  variants,
  sizeGuide,
  sizeGuideDisabled,
  productType,
  defaultVariant,
  collectionsHandles,
  trackingData,
  checkoutItems,
  canShowFixedPanel,
  isOutOfStock,
  productOutOfStock,
  product,
}: StickyAddToCartPopinProps) {
  const t = useTranslate()
  const [shouldOpenFixedPanel, setShouldOpenFixedPanel] = useIsFixedPanelOpen()
  const fixedPanelRef = useRef(null)
  const truncatedTitle = truncateText({ text: product.title ?? undefined, type: "paragraph" }, 30)
  const { width } = useWindowSize()
  if (width >= breakpoints.lg) {
    setShouldOpenFixedPanel(false)
    document.body.style.overflow = ""
  }

  const handleAddToCart = () => {
    setShouldOpenFixedPanel(true)
    document.body.style.overflow = "hidden"
  }

  const handleCloseOfFixedPanel = () => {
    setShouldOpenFixedPanel(false)
    document.body.style.overflow = ""
  }

  return (
    <div
      data-comp={"UI/ProductHeader/ProductDetailsCta/StickyAddToCartPopin"}
      ref={fixedPanelRef}
      className={clsx(css.withSizeGuidePopinWrapper, hideFrom("desktop", "flex"), sprinkles({ p: 10 }))}
    >
      <div className={clsx(css.panel({ active: shouldOpenFixedPanel && canShowFixedPanel }))}>
        {activeVariants && (
          <VariantsRowWithVariantSwitch
            className={clsx(sprinkles({ textAlign: "center", marginBottom: 30 }))}
            variants={variants ?? []}
            stockAlertEnabled={shouldDisplayStockAlert}
            productType={productType ?? null}
            sizeGuideDisabled={sizeGuideDisabled}
            defaultVariant={defaultVariant ?? null}
            isInAddToCartPopin
            collectionsHandles={collectionsHandles ?? []}
            sizeGuide={sizeGuide}
            trackingData={trackingData}
            product={product}
            label={t("cart_fixed_popin_size_label")}
          />
        )}
        <CloseButton onClick={handleCloseOfFixedPanel} className={css.closeButton} />
      </div>
      <div
        className={clsx(
          sprinkles({ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 })
        )}
      >
        <p className={clsx(text({ design: "neue-16" }))}>{truncatedTitle?.data?.text}</p>
        <ProductDetailsPriceWithVariantSwitch
          variants={Array.isArray(activeVariants) ? activeVariants : activeVariants !== null ? [activeVariants] : []}
          defaultVariant={defaultVariant ?? null}
        />
      </div>
      <AddToCartCta
        className={clsx(
          css.cta,
          text({ design: "neue-11-13" }),
          sprinkles({
            pY: { mobile: 10, tablet: 12 },
          })
        )}
        overrideTextPresets
        isOutOfStock={isOutOfStock}
        onClick={handleAddToCart}
        productOutOfStock={productOutOfStock}
        activeVariants={activeVariants}
        checkoutItems={checkoutItems}
        onSuccess={handleCloseOfFixedPanel}
      />
    </div>
  )
}
